'use client';

import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useCallback } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  AccordionContent,
  Accordion,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion.component';
import { Link } from '@/components/ui/link/link.component';
import { shouldHideNav } from '@/utils/navbar.util';
import { HeaderNavItem } from '@/app/(main)/_components/sign-in-modal/_types/header.types';

interface navbarSheetProps {
  navItems: HeaderNavItem[];
}

export function NavbarSheet({ navItems }: navbarSheetProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  if (shouldHideNav(pathname)) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          className="lg:hidden"
          aria-label="Abrir menu"
          data-testid={DataTestIdEnum.HEADER_HUMBURGER_BUTTON}
        >
          <Icon
            icon={faBars}
            className="text-gray-700 ml-[-0.3125rem]"
            size="lg"
          />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        closingTagPosition="left"
        className="w-full p-0"
      >
        <Accordion
          type="single"
          collapsible
          className="mt-20"
          defaultValue="Para você"
        >
          <nav>
            {navItems.map((navbarItem) => (
              <AccordionItem
                value={navbarItem.normalLabel}
                key={navbarItem.normalLabel}
              >
                <AccordionTrigger
                  className="mb-4 mx-5"
                  data-testid={navbarItem.dataTestId}
                >
                  <Icon
                    icon={navbarItem.icon}
                    className="text-gray-900 text-lg w-5 h-5 font-light"
                  />
                  <div className="!justify-start w-full flex ml-2 text-lg font-semibold text-gray-900">
                    {navbarItem.normalLabel}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="!text-left !justify-start pb-4">
                  <ul className="w-full">
                    {navbarItem.subItems.map((subItem) => (
                      <li key={subItem.label}>
                        {subItem.url
                          ? (
                              <Link
                                prefetch={false}
                                role="menuitem"
                                href={subItem.url}
                                className="py-4 flex w-full !justify-start !place-content-start pl-12 text-gray-800 font-normal text-lg focus:bg-gray-50 focus:text-success-700 transition-colors duration-300"
                                data-testid={subItem.dataTestId}
                                target={subItem.target}
                                onClick={close}
                              >
                                {subItem.label}
                              </Link>
                            )
                          : (
                              <button
                                role="menuitem"
                                onClick={subItem.onClick}
                                className="py-4 flex w-full !justify-start !place-content-start pl-12 text-gray-800 font-normal text-lg focus:bg-gray-50 focus:text-success-700 transition-colors duration-300"
                                data-testid={subItem.dataTestId}
                              >
                                {subItem.label}
                              </button>
                            )}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </nav>
        </Accordion>
      </SheetContent>
    </Sheet>
  );
}
